import { useContext, useEffect, useRef, useState } from 'react';
import { SearchFilterContext } from '../contexts/searchFilter.context';
import { MapControlContext } from '../contexts/mapcontrol.context';
import useCallSearchAssets from './useCallSearchAssets';
import {
  checkIfAssetUserCheckboxesHaveChangedSinceLastSearch,
  checkIfMobileFiltersHaveChangedSinceLastSearch,
  checkIfSearchTermHasChangedSinceLastSearch,
  getArrayOfValuesFromOptions,
} from '../utils/utils';
import { options } from '../components/mobile/MobileAssetsPeopleCheckBoxGroup';
import { FullConfigContext } from 'contexts/fullConfig.context';

// a hook to contain filtering functions used for mobile nav panel for map.
export default function useMobileFilterPanel({
  setMobileMapFilterMenuOpen,
}: {
  setMobileMapFilterMenuOpen: (arg: boolean) => void;
}) {
  const defaultAssetPeopleOptions = getArrayOfValuesFromOptions(options);

  const [noFilterResultsReturned, setNoFilterResultsReturned] = useState(false);
  const [noSearchResultsReturned, setNoSearchResultsReturned] = useState(false);
  const [assetUserCheckboxArray, setAssetUserCheckboxArray] =
    useState<any>(defaultAssetPeopleOptions);

  const {
    searchTerm,
    setSearchTerm,
    setDoNotCalculateFloor,
    setInSearchMode,
    setInFilterMode,
    inSearchMode,
    inFilterMode,
    setAssetOwnerCheckedListArray,
    setAssetSubtypeCheckedListArray,
    setAssetStatusCheckedListArray,
    setAssetStatusCheckAll,
    setAssetSubtypeCheckAll,
    setAssetOwnerCheckAll,
    filtersModified,
    assetOwnerCheckedListArray,
    assetSubtypeCheckedListArray,
    assetStatusCheckedListArray,
    defaultAssetOwnerCheckedListArray,
    defaultAssetSubtypeCheckedListArray,
    defaultAssetStatusCheckedListArray,
    assetOwnerCheckedListArrayRef,
    assetSubtypeCheckedListArrayRef,
    assetStatusCheckedListArrayRef,
  } = useContext(SearchFilterContext);

  const { setRecallGetAssets, setRecallGetUsers, setShowAssetsChecked, setShowUsersChecked } =
    useContext(MapControlContext);
  const { availableFeaturesObject } = useContext(FullConfigContext);
  const hasAssetStatus = availableFeaturesObject.asset_status;
  const { callSearchAssets, callFilterAssets } = useCallSearchAssets();
  const searchTermRef: any = useRef('');

  const assetUserCheckboxArrayRef: any = useRef(assetUserCheckboxArray);

  function resetSearchFromCheckbox() {
    setSearchTerm('');
    setNoSearchResultsReturned(false);
  }

  function handleMobileSearchFilters() {
    const hasSearchTerm = searchTerm.length > 0;
    const assetsChecked = assetUserCheckboxArray.includes('assets');
    const usersChecked = assetUserCheckboxArray.includes('people');

    if (hasSearchTerm) {
      callSearchAssets(searchTerm).then((items) => {
        const noResults = items === undefined || items.length < 1;
        setNoSearchResultsReturned(noResults);
        setInSearchMode(!noResults); // only is set if there are results.
        setInFilterMode(false);
        searchTermRef.current = searchTerm;
        assetOwnerCheckedListArrayRef.current = defaultAssetOwnerCheckedListArray;
        assetSubtypeCheckedListArrayRef.current = defaultAssetSubtypeCheckedListArray;
        assetStatusCheckedListArrayRef.current = defaultAssetStatusCheckedListArray;
        setAssetOwnerCheckAll(true);
        setAssetSubtypeCheckAll(true);

        // do not close menu if there are no results from search.
        if (!noResults) {
          setMobileMapFilterMenuOpen(false);
        }
      });
    } else if (filtersModified) {
      callFilterAssets(
        null,
        assetOwnerCheckedListArray,
        assetSubtypeCheckedListArray,
        assetStatusCheckedListArray,
      ).then((filteredItems) => {
        const noResults = filteredItems === undefined || filteredItems.length < 1;

        setNoFilterResultsReturned(noResults);
        setInFilterMode(!noResults); // only is set if there are results
        setInSearchMode(false);
        // store checkedlists in refs after search.
        assetOwnerCheckedListArrayRef.current = assetOwnerCheckedListArray;
        assetSubtypeCheckedListArrayRef.current = assetSubtypeCheckedListArray;
        assetStatusCheckedListArrayRef.current = assetStatusCheckedListArray;
        searchTermRef.current = ''; // reset search after filter takes place.

        // do not close menu if there are no results from search.
        if (!noResults) {
          setMobileMapFilterMenuOpen(false);
        }
      });
    } else {
      // close menu and recall assets if no searchterm or filters exist
      setMobileMapFilterMenuOpen(false);
      setInSearchMode(false);
      setInFilterMode(false);
      setRecallGetAssets(true);
      setRecallGetUsers(true);
      setDoNotCalculateFloor(true); // set to true so we dont recenter map / floor
      setNoFilterResultsReturned(false);
      setNoSearchResultsReturned(false);
    }
    assetUserCheckboxArrayRef.current = assetUserCheckboxArray;

    setNoSearchResultsReturned(false);
    setNoFilterResultsReturned(false);
    setShowAssetsChecked(assetsChecked);
    setShowUsersChecked(usersChecked);
  }

  function handleClosePanel() {
    const filtersShouldRevertToRefs = checkIfMobileFiltersHaveChangedSinceLastSearch(
      assetOwnerCheckedListArray,
      assetSubtypeCheckedListArray,
      assetOwnerCheckedListArrayRef.current,
      assetSubtypeCheckedListArrayRef.current,
      hasAssetStatus ? assetStatusCheckedListArray : null,
      hasAssetStatus ? assetStatusCheckedListArrayRef.current : null,
    );

    const assetUserCheckboxesShouldRevert = checkIfAssetUserCheckboxesHaveChangedSinceLastSearch(
      assetUserCheckboxArray,
      assetUserCheckboxArrayRef.current,
    );
    // do not reset search, if search or filter mode is active.
    if (inFilterMode) {
      setMobileMapFilterMenuOpen(false);
      if (searchTerm.length > 0) {
        setSearchTerm('');
      }
    } else if (inSearchMode) {
      setMobileMapFilterMenuOpen(false);
    } else {
      // full reset
      setMobileMapFilterMenuOpen(false);
      setInSearchMode(false);
      setInFilterMode(false);
      setRecallGetAssets(true);
      setRecallGetUsers(true);
      setDoNotCalculateFloor(true); // set to true so we dont recenter map / floor
      setNoFilterResultsReturned(false);
      setNoSearchResultsReturned(false);
      setSearchTerm('');
      searchTermRef.current = '';
    }

    // revert filters to last search values, if user has modified, not searched and closes menu.
    if (filtersShouldRevertToRefs) {
      setAssetOwnerCheckedListArray(assetOwnerCheckedListArrayRef.current);
      setAssetSubtypeCheckedListArray(assetSubtypeCheckedListArrayRef.current);
      setAssetStatusCheckedListArray(assetSubtypeCheckedListArrayRef.current);

      setAssetSubtypeCheckAll(
        assetSubtypeCheckedListArrayRef.current.length ===
          defaultAssetSubtypeCheckedListArray.length,
      );
    }
    setAssetOwnerCheckAll(
      assetOwnerCheckedListArrayRef.current.length === defaultAssetOwnerCheckedListArray.length,
    );
    setAssetStatusCheckAll(
      assetSubtypeCheckedListArrayRef.current.length === defaultAssetStatusCheckedListArray.length,
    );
    if (checkIfSearchTermHasChangedSinceLastSearch(searchTerm, searchTermRef.current)) {
      setSearchTerm(searchTermRef.current);
    }

    if (assetUserCheckboxesShouldRevert) {
      setAssetUserCheckboxArray(assetUserCheckboxArrayRef.current);
    }
  }

  useEffect(() => {
    // reset no search panel when checkboxes change.
    setNoFilterResultsReturned(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetOwnerCheckedListArray, assetSubtypeCheckedListArray, assetStatusCheckedListArray]);

  useEffect(() => {
    // reset search when searchterm changes.
    setNoSearchResultsReturned(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  useEffect(() => {
    // set refs to default on load.
    assetOwnerCheckedListArrayRef.current = defaultAssetOwnerCheckedListArray;
    assetSubtypeCheckedListArrayRef.current = defaultAssetSubtypeCheckedListArray;
    assetStatusCheckedListArrayRef.current = defaultAssetStatusCheckedListArray;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    resetSearchFromCheckbox,
    handleMobileSearchFilters,
    handleClosePanel,
    setNoFilterResultsReturned,
    setAssetUserCheckboxArray,
    noSearchResultsReturned,
    noFilterResultsReturned,
    setNoSearchResultsReturned,
    assetUserCheckboxArray,
  };
}
